import { clear_auth_cache, has_any_acl, refresh_auth } from './lib/auth'
import { classify_api_failure } from './lib/api-failure'
import { bind_failure_banner, failure_banner_html } from './lib/collection-load'
import { advertiser_is_logged_in } from './lib/advertiser-auth'

export { clear_auth_cache } from './lib/auth'

export interface RouteContext {
  path: string
  params: Record<string, string>
  query: URLSearchParams
}

export interface PageModule {
  mount(el: HTMLElement, ctx?: RouteContext): void | Promise<void>
  unmount?(): void
}

type Route = {
  path: string
  pattern: RegExp
  param_names: string[]
  loader: () => Promise<PageModule>
  public?: boolean
  anonymous?: boolean
  /** F6.2 — terceira classe: exige sessão de ANUNCIANTE, não do operador. */
  advertiser?: boolean
  acl_any?: string[]
}

function compile_path(path: string): { pattern: RegExp; param_names: string[] } {
  const param_names: string[] = []
  const re_body = path.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_, name) => {
    param_names.push(name)
    return '([^/]+)'
  })
  return { pattern: new RegExp(`^${re_body}$`), param_names }
}

function match_route(routes: Route[], pathname: string): { route: Route; params: Record<string, string> } | null {
  for (const route of routes) {
    const match = route.pattern.exec(pathname)
    if (!match) continue
    const params: Record<string, string> = {}
    route.param_names.forEach((name, idx) => {
      params[name] = decodeURIComponent(match[idx + 1] || '')
    })
    return { route, params }
  }
  return null
}

class Router {
  private routes: Route[] = []
  private current_module: PageModule | null = null
  private container: HTMLElement | null = null

  register(
    path: string,
    loader: Route['loader'],
    options: {
      public?: boolean
      anonymous?: boolean
      acl_any?: string[]
      /**
       * TERCEIRA classe de guarda (F6.2): exige sessão de ANUNCIANTE.
       *
       * Não é `public` (precisa de sessão) nem protegida por ACL (não existe tag
       * do lado do anunciante). O universo é outro, e por isso a opção é outra —
       * marcar uma rota de anunciante como protegida por ACL a mandaria para o
       * `/login` do operador.
       */
      advertiser?: boolean
    } = {}
  ): void {
    const { pattern, param_names } = compile_path(path)
    this.routes.push({
      path,
      pattern,
      param_names,
      loader,
      public: options.public === true,
      anonymous: options.anonymous === true,
      advertiser: options.advertiser === true,
      acl_any: options.acl_any || []
    })
  }

  setup(container: HTMLElement): void {
    this.container = container
    window.addEventListener('popstate', () => {
      void this.navigate(window.location.pathname + window.location.search, false)
    })

    document.addEventListener('click', (event) => {
      const anchor = (event.target as HTMLElement).closest('a') as HTMLAnchorElement | null
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#') || href.startsWith('http') || href.startsWith('//') || href.startsWith('mailto:') || href.startsWith('tel:')) return
      if (anchor.hasAttribute('target') || anchor.hasAttribute('download')) return
      if (event.ctrlKey || event.metaKey || event.shiftKey) return
      event.preventDefault()
      void this.navigate(href)
    })
  }

  /**
   * Navegação que falha VISIVELMENTE.
   *
   * `refresh_auth()` e o `mount()` da página batem na API. Com a API fora, a
   * promessa rejeitava sem ninguém escutando: o clique não fazia nada e a tela
   * antiga continuava lá, como se o usuário não tivesse clicado. Agora a falha
   * vira banner com Recarregar — mesmo padrão das listas.
   */
  async navigate(path: string, push = true): Promise<void> {
    try {
      await this.navigate_inner(path, push)
    } catch (err) {
      const failure = classify_api_failure(err)
      // 401 já foi tratado pelo observador global (que redireciona ao login);
      // pintar banner por cima disputaria com o redirecionamento.
      if (failure.kind === 'unauthorized') return
      if (!this.container) throw err

      this.container.innerHTML = `<main class="grid min-h-screen place-items-start bg-canvas p-6 text-ink">
        <section class="w-full max-w-xl">${failure_banner_html('esta página', failure)}</section>
      </main>`
      const slot = this.container.querySelector<HTMLElement>('[data-failure-banner]')?.parentElement
      if (slot) bind_failure_banner(slot, () => window.location.reload())
    }
  }

  private async navigate_inner(path: string, push: boolean): Promise<void> {
    if (!this.container) throw new Error('Router not initialized')

    const clean = path.split('?')[0].split('#')[0]
    const query = new URLSearchParams(path.includes('?') ? path.slice(path.indexOf('?') + 1).split('#')[0] : '')
    const match = match_route(this.routes, clean)
    const route = match?.route || null
    const params = match?.params || {}

    if (!route) {
      if (push) window.history.pushState({}, '', path)
      this.container.innerHTML = `
        <main class="grid min-h-screen place-items-center bg-canvas p-6 text-ink">
          <section class="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-card">
            <h1 class="text-xl font-semibold text-ink">404</h1>
            <p class="mt-2 text-sm text-muted">Página não encontrada.</p>
            <a class="mt-5 inline-flex h-10 items-center rounded-md bg-brand px-4 text-sm font-semibold text-brand-ink" href="/">Voltar para o início</a>
          </section>
        </main>`
      return
    }

    // Rota de anunciante NÃO chama `refresh_auth()`: essa função consulta a
    // sessão do OPERADOR, e misturar as duas é exatamente o que o modelo de
    // universos separados existe para não fazer. Também não passa pelo bloco de
    // ACL abaixo — não existe tag do lado do anunciante.
    if (route.advertiser) {
      if (!(await advertiser_is_logged_in())) {
        await this.navigate_inner('/anunciante/entrar', true)
        return
      }
    }

    const user = route.advertiser || route.anonymous ? null : await refresh_auth()

    if (!route.advertiser && !route.anonymous) {
      if (!route.public && !user) {
        await this.navigate_inner('/login', true)
        return
      }
      if (route.public && user && ['/login', '/register', '/forgot-password'].includes(clean)) {
        await this.navigate_inner('/adm', true)
        return
      }
      if (!route.public && user?.must_change_password && clean !== '/conta') {
        await this.navigate_inner('/conta?must-change=1', true)
        return
      }
      if (!route.public && route.acl_any?.length && !has_any_acl(user, route.acl_any)) {
        if (push) window.history.pushState({}, '', path)
        this.container.innerHTML = `
          <main class="grid min-h-screen place-items-center bg-canvas p-6 text-ink">
            <section class="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-card">
              <h1 class="text-xl font-semibold text-ink">403</h1>
              <p class="mt-2 text-sm text-muted">Você não tem permissão para acessar esta área.</p>
              <a class="mt-5 inline-flex h-10 items-center rounded-md bg-brand px-4 text-sm font-semibold text-brand-ink" href="/">Voltar para o início</a>
            </section>
          </main>`
        return
      }
    }

    if (push) window.history.pushState({}, '', path)
    if (this.current_module?.unmount) this.current_module.unmount()
    this.container.innerHTML = ''
    const mod = await route.loader()
    this.current_module = mod
    await mod.mount(this.container, { path: clean, params, query })
  }
}

export const router = new Router()

// Páginas públicas (anônimas — sem checagem de auth)
router.register('/', () => import('./pages/public-home'), { anonymous: true })
router.register('/anuncio/:short_code', () => import('./pages/public-ad'), { anonymous: true })
// Público e sem sessão: o único insumo é o token de uso único que chegou por
// e-mail. Confirmar o e-mail NÃO cria conta (F6.1).
router.register('/verificar-email', () => import('./pages/public-verify-email'), { public: true })
// Termos e privacidade (F8.2). Públicos e anônimos: exigir sessão para ler o que
// se está aceitando seria absurdo.
router.register('/termos', () => import('./pages/public-legal'), { public: true, anonymous: true })
router.register('/privacidade', () => import('./pages/public-legal'), { public: true, anonymous: true })
// Conta de anunciante (F6.2). `advertiser: true` é a TERCEIRA classe de guarda:
// não é `public` (precisa de sessão) nem protegida por ACL (não existe tag do
// lado do anunciante).
router.register('/anunciante/entrar', () => import('./pages/advertiser-login'), { public: true })
router.register('/anunciante/conta', () => import('./pages/advertiser-account'), { advertiser: true })
router.register('/anunciante/anuncios', () => import('./pages/advertiser-ads'), { advertiser: true })
router.register('/publicar', () => import('./pages/public-submit'), { anonymous: true })
router.register('/minha-conta', () => import('./pages/public-my-account'), { anonymous: true })

// Páginas de autenticação (públicas, mas tratadas pelo guard)
router.register('/login', () => import('./pages/login'), { public: true })
router.register('/register', () => import('./pages/register'), { public: true })
router.register('/forgot-password', () => import('./pages/forgot-password'), { public: true })
router.register('/first-access', () => import('./pages/first-access'), { public: true })
router.register('/reset-password', () => import('./pages/reset-password'), { public: true })

// Painel admin em /adm/*
router.register('/adm', () => import('./pages/adm-home'), { acl_any: ['ads:list', 'ads:moderate', 'users:list', 'users:manage', 'acl:manage', 'pricing:read', 'pricing:manage', 'categories:read', 'categories:manage', 'subcategories:read', 'subcategories:manage', 'ad_types:read', 'ad_types:manage', 'contacts:read'] })
router.register('/adm/anuncios', () => import('./pages/adm-ads'), { acl_any: ['ads:list', 'ads:moderate'] })
router.register('/adm/categorias', () => import('./pages/adm-categories'), { acl_any: ['categories:read', 'categories:manage'] })
router.register('/adm/subcategorias', () => import('./pages/adm-subcategories'), { acl_any: ['subcategories:read', 'subcategories:manage'] })
router.register('/adm/tipos', () => import('./pages/adm-ad-types'), { acl_any: ['ad_types:read', 'ad_types:manage'] })
router.register('/adm/precos', () => import('./pages/adm-pricing'), { acl_any: ['pricing:read', 'pricing:manage'] })
router.register('/adm/contatos', () => import('./pages/adm-contacts'), { acl_any: ['contacts:read'] })
// Guarda no ROUTER, não só no menu: sem ela a tela abre para quem tem sessão e
// só o 403 da API a esvaziaria — `403` não é mecanismo de UX (AGENTS-ACL §1.3).
router.register('/adm/denuncias', () => import('./pages/adm-reports'), { acl_any: ['ads:reports'] })
router.register('/adm/moderacao', () => import('./pages/adm-moderation'), { acl_any: ['moderation:rules_read', 'moderation:rules_manage'] })
router.register('/adm/auditoria', () => import('./pages/adm-audit'), { acl_any: ['audit:read'] })
router.register('/adm/privacidade', () => import('./pages/adm-privacy'), { acl_any: ['privacy:manage'] })
router.register('/adm/usuarios', () => import('./pages/admin-users'), { acl_any: ['users:list', 'users:manage'] })
router.register('/adm/acl', () => import('./pages/admin-acl'), { acl_any: ['acl:manage'] })
router.register('/conta', () => import('./pages/account'), { acl_any: ['account:self'] })

// Compatibilidade com URLs antigas (redireciona pra nova)
router.register('/admin/users', () => import('./pages/admin-users'), { acl_any: ['users:list', 'users:manage'] })
router.register('/admin/acl', () => import('./pages/admin-acl'), { acl_any: ['acl:manage'] })
router.register('/account', () => import('./pages/account'), { acl_any: ['account:self'] })
